import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, input, signal } from '@angular/core';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { IconType } from 'lib/ui/icons/index.js';
import { IViewOrEditWorkoutPlanRouteData } from 'src/app/routes/trainer/workout-routes.js';
import { workoutHelper } from './workout.helper.js';

@Component({
	templateUrl: './workout-form.component.html',
	selector: 'mod-workout-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class WorkoutFormComponent extends TypeCoreComponent<IViewOrEditWorkoutPlanRouteData> implements OnInit {
	public readonly isInDialog = input<boolean>(false);
	public readonly availableIconTypes: IconType[] = workoutHelper.getWorkoutIconTypes();

	public readonly form = {
		name: signal<string | undefined>(undefined),
		notes: signal<string | undefined>(undefined),
		warmUp: signal<string | undefined>(undefined),
		recommendedLength: signal<string | undefined>(undefined),
		cooldown: signal<string | undefined>(undefined),
		iconType: signal<IconType | undefined>(undefined),
		avatar: signal<CosmosDb.ICosmosFileReference[]>([])
	};

	public readonly uploadFunction: (file: File) => Observable<CosmosDb.ICosmosFileReference[]> = (file) => {
		return this.dependencies.httpService
			.uploadFile({
				file: file,
				type: 'workout'
			})
			.pipe(
				map((response) => {
					return [...this.form.avatar(), response.data];
				})
			);
	};

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.sharedData()?.data?.workout?.id) {
			return true;
		}
		return false;
	});

	public readonly showIconSelectionField: Signal<boolean> = computed(() => {
		// do not show icon selection field at all - in favor of images

		// if (this.sharedData()?.data.workoutType === 'activity') {
		// 	return true;
		// }
		return false;
	});

	public readonly showInstructionFields: Signal<boolean> = computed(() => {
		if (this.sharedData()?.data?.workoutType === 'workout') {
			return true;
		}
		return false;
	});

	public readonly workoutOrActivitySingular: Signal<string> = computed(() => {
		if (this.sharedData()?.data?.workoutType === 'workout') {
			return 'Workout';
		}
		return 'Activity';
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const name = this.form.name();

		if (!name) {
			return undefined;
		}

		const sharedData = this.sharedData();
		const clientData = sharedData?.data?.clientData;

		if (!sharedData) {
			return undefined;
		}

		// save in client
		if (clientData) {
			const trainingContainers: readonly UserDb.IUserTrainingContainer[] =
				clientData.client.training?.containers ?? [];

			const trainingContainer = trainingContainers.find((m) => m.id === clientData.trainingContainer.id);

			if (!trainingContainer) {
				throw new Error(`Invalid training container with id '${clientData.trainingContainer.id}'`);
			}

			const containerItemIndex = trainingContainer.items.findIndex((m) => m.id === clientData.trainingItem.id);

			// update workout
			trainingContainer.items[containerItemIndex].workout.system.name = name;
			trainingContainer.items[containerItemIndex].workout.notes = this.form.notes();
			trainingContainer.items[containerItemIndex].workout.avatar = this.form.avatar();

			// save user
			return this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditTrainingPlanRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editUserTraining')
						.withId(clientData.client.id)
						.withData({
							title: clientData.client.training?.title ?? 'default',
							trainingContainers: trainingContainers
						})
						.build()
				)
				.pipe(
					map((response) => {
						super.notifySaved('Workout');
						super.updateSharedData({
							linkedItems: sharedData.linkedItems ?? [],
							data: {
								workoutType: sharedData.data.workoutType ?? 'workout',
								workout: trainingContainer.items[containerItemIndex].workout,
								clientData: sharedData.data.clientData,
								sessionForDate: sharedData.data.sessionForDate
							}
						});
					})
				);
		}

		// default workout save
		return this.dependencies.httpService
			.upsert<WorkoutDb.IWorkoutItem, WorkoutDb.IBaseWorkoutItem>({
				type: 'workout',
				existingItem: sharedData.data.workout,
				createModel: {
					exerciseContainers: [],
					name: name,
					type: sharedData.data.workoutType,
					cooldown: this.form.cooldown(),
					warmUp: this.form.warmUp(),
					notes: this.form.notes(),
					iconType: this.form.iconType(),
					avatar: this.form.avatar(),
					recommendedLength: this.form.recommendedLength()
				},
				patchModel: (item) => {
					return {
						id: item.id,
						name: name,
						cooldown: this.form.cooldown(),
						warmUp: this.form.warmUp(),
						notes: this.form.notes(),
						iconType: this.form.iconType(),
						avatar: this.form.avatar(),
						recommendedLength: this.form.recommendedLength()
					};
				}
			})
			.pipe(
				map((response) => {
					const isEditForm = this.isEditForm();
					super.notifyUpsert('Workout', isEditForm);
					super.updateSharedData({
						data: {
							workout: response.data,
							workoutType: sharedData.data.workoutType ?? 'workout',
							clientData: clientData,
							sessionForDate: sharedData.data.sessionForDate
						},
						linkedItems: sharedData?.linkedItems ?? []
					});

					if (!isEditForm) {
						if (response.data.type === 'workout') {
							this.dependencies.navigateService
								.actionRoute((routes) => routes.workoutRoutes.workout.children.view, response.data.id)
								.navigate();
						} else if (response.data.type === 'activity') {
							this.dependencies.navigateService
								.actionRoute((routes) => routes.workoutRoutes.activity.children.edit, response.data.id)
								.navigate();
						}
					}
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			if (sharedData?.data.workout) {
				this.form.name.set(sharedData.data.workout.system.name);
				this.form.recommendedLength.set(sharedData.data.workout.recommendedLength);
				this.form.notes.set(sharedData.data.workout.notes);
				this.form.iconType.set(sharedData.data.workout.iconType);
				this.form.cooldown.set(sharedData.data.workout.cooldown);
				this.form.warmUp.set(sharedData.data.workout.warmUp);
				this.form.avatar.set(sharedData.data.workout.avatar ?? []);
			}
		};
	}

	handleUploadImage(files: CosmosDb.ICosmosFileReference[] | undefined): void {
		if (files) {
			this.form.avatar.set(files);
			const saveHandler = this.saveHandler();

			if (saveHandler && this.isEditForm()) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
